import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAcceptedConnectionsForUser, getTaskTitleForConnection, getDisplayNameForUser } from '../api/connections';
import { ConnectionRequest } from '../types';
import { Link } from 'react-router-dom';
import { Users, FileText, ExternalLink, MessageCircle, ArrowLeft, ChevronDown, Layers } from 'lucide-react';
import TaskChat from '../components/TaskChat';

interface EnrichedConnection {
  connection: ConnectionRequest;
  taskTitle: string;
  otherUserName: string;
  otherUserId: string;
}

interface GroupedUser {
  userId: string;
  userName: string;
  connections: EnrichedConnection[];
  latestActivity: number;
}

const TaskBuddies = () => {
  const { currentUser } = useAuth();
  const [connections, setConnections] = useState<EnrichedConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeChat, setActiveChat] = useState<EnrichedConnection | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  useEffect(() => {
    async function loadConnections() {
      if (!currentUser) return;
      try {
        const rawConnections = await getAcceptedConnectionsForUser(currentUser.uid);
        const enriched = await Promise.all(
          rawConnections.map(async (conn) => {
            const taskTitle = await getTaskTitleForConnection(conn.taskId);
            const otherUserId = conn.taskerId === currentUser.uid ? conn.taskOwnerId : conn.taskerId;
            const otherUserName = await getDisplayNameForUser(otherUserId, 'Unknown User');
            return { connection: conn, taskTitle, otherUserId, otherUserName };
          })
        );
        setConnections(enriched);
      } catch (err) {
        console.error('Failed to load connections', err);
      } finally {
        setLoading(false);
      }
    }
    loadConnections();
  }, [currentUser]);

  // Group connections by other user
  const groupedUsers = useMemo(() => {
    const map = new Map<string, GroupedUser>();
    connections.forEach((item) => {
      const existing = map.get(item.otherUserId);
      if (existing) {
        existing.connections.push(item);
        const ts = item.connection.updatedAt ? new Date(item.connection.updatedAt).getTime() : 0;
        if (ts > existing.latestActivity) existing.latestActivity = ts;
      } else {
        map.set(item.otherUserId, {
          userId: item.otherUserId,
          userName: item.otherUserName,
          connections: [item],
          latestActivity: item.connection.updatedAt ? new Date(item.connection.updatedAt).getTime() : 0,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.latestActivity - a.latestActivity);
  }, [connections]);

  const uniqueUserCount = groupedUsers.length;

  // Auto-expand user when they only have one task
  const handleUserClick = (user: GroupedUser) => {
    if (user.connections.length === 1) {
      setActiveChat(user.connections[0]);
      setExpandedUser(user.userId);
    } else {
      setExpandedUser((prev) => (prev === user.userId ? null : user.userId));
    }
  };

  const handleTaskSelect = (item: EnrichedConnection) => {
    setActiveChat(item);
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-tech-cyan/20 border-t-tech-cyan" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden">
      {/* Header */}
      <div className="shrink-0 border-b border-white/[0.07] bg-tech-surface/90 backdrop-blur-sm px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          {activeChat && (
            <button
              type="button"
              onClick={() => setActiveChat(null)}
              className="lg:hidden p-2 rounded-lg text-tech-text-muted hover:text-tech-cyan hover:bg-white/[0.04] transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-tech-cyan/10 border border-tech-cyan/20 shrink-0">
            <MessageCircle className="h-4.5 w-4.5 text-tech-cyan" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-lg font-bold tracking-tight text-white">
              {activeChat ? activeChat.otherUserName : 'TaskBuddies'}
            </h1>
            <p className="text-xs text-tech-text-muted truncate">
              {activeChat
                ? activeChat.taskTitle
                : `${uniqueUserCount} ${uniqueUserCount === 1 ? 'person' : 'people'} · ${connections.length} task connection${connections.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {activeChat && (
            <Link
              to={`/tasks/${activeChat.connection.taskId}`}
              className="ml-auto text-xs text-tech-cyan hover:text-tech-text flex items-center gap-1 transition-colors shrink-0"
            >
              View Task <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>

      {connections.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-tech-cyan/10 border border-tech-cyan/20">
            <Users className="h-8 w-8 text-tech-cyan" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-white">No connections yet</h3>
          <p className="max-w-sm text-sm text-tech-text-muted mb-6">
            Connect with people on tasks in the Marketplace. Accepted connections appear here with a chat window.
          </p>
          <div className="flex gap-3">
            <Link to="/task-hatch"
              className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-tech-dark transition-all"
              style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}>
              Create a task
            </Link>
            <Link to="/marketplace"
              className="inline-flex items-center justify-center rounded-xl border border-tech-border bg-tech-gray/40 px-4 py-2.5 text-sm font-medium text-tech-text hover:border-tech-cyan/30 transition-all">
              Find tasks
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Contact list — grouped by user */}
          <div className={`${activeChat ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-80 xl:w-96 shrink-0 border-r border-white/[0.07] overflow-hidden`}>
            <div className="px-4 py-3 border-b border-white/[0.04]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-tech-text-muted/80">Connections</p>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {groupedUsers.map((user) => {
                const isExpanded = expandedUser === user.userId;
                const hasMultiple = user.connections.length > 1;
                const latestDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
                  .format(new Date(user.latestActivity));

                return (
                  <div key={user.userId}>
                    {/* User row */}
                    <button
                      type="button"
                      onClick={() => handleUserClick(user)}
                      className={`w-full flex items-start gap-3 px-4 py-3.5 text-left transition-all border-b border-white/[0.04] ${
                        (activeChat?.otherUserId === user.userId && !hasMultiple)
                          ? 'bg-tech-cyan/5 border-l-2 border-l-tech-cyan'
                          : 'hover:bg-white/[0.03] border-l-2 border-l-transparent'
                      }`}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-tech-cyan/20 to-blue-500/15 border border-tech-cyan/20 text-sm font-bold text-tech-cyan">
                        {user.userName.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <h4 className={`text-sm font-semibold truncate ${activeChat?.otherUserId === user.userId ? 'text-tech-cyan' : 'text-tech-text'}`}>
                            {user.userName}
                          </h4>
                          <span className="text-[10px] text-tech-text-muted shrink-0 ml-2">{latestDate}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Layers className="w-3 h-3 text-tech-text-muted/60 shrink-0" />
                          <span className="text-xs text-tech-text-muted truncate">
                            {user.connections.length} task{user.connections.length !== 1 ? 's' : ''}
                          </span>
                          {hasMultiple && (
                            <ChevronDown className={`w-3 h-3 text-tech-text-muted shrink-0 ml-auto transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Expanded task list for multi-task users */}
                    {hasMultiple && isExpanded && (
                      <div className="bg-white/[0.02]">
                        {user.connections.map((item) => {
                          const isActive = activeChat?.connection.id === item.connection.id;
                          return (
                            <button
                              key={item.connection.id}
                              type="button"
                              onClick={() => handleTaskSelect(item)}
                              className={`w-full flex items-center gap-2.5 pl-16 pr-4 py-2.5 text-left transition-all border-b border-white/[0.02] ${
                                isActive
                                  ? 'bg-tech-cyan/8 border-l-2 border-l-tech-cyan'
                                  : 'hover:bg-white/[0.03] border-l-2 border-l-transparent'
                              }`}
                            >
                              <FileText className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-tech-cyan' : 'text-tech-text-muted/60'}`} />
                              <span className={`text-xs truncate ${isActive ? 'text-tech-cyan font-medium' : 'text-tech-text-muted'}`}>
                                {item.taskTitle}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Chat area */}
          <div className={`${activeChat ? 'flex' : 'hidden lg:flex'} flex-col flex-1 min-w-0`}>
            {activeChat ? (
              <TaskChat
                connectionId={activeChat.connection.id}
                taskId={activeChat.connection.taskId}
                otherParticipantName={activeChat.otherUserName}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 rounded-2xl bg-tech-cyan/5 border border-tech-cyan/10 flex items-center justify-center mb-4">
                  <MessageCircle className="w-8 h-8 text-tech-cyan/40" />
                </div>
                <h3 className="text-base font-semibold text-tech-text mb-1">Select a conversation</h3>
                <p className="text-sm text-tech-text-muted max-w-xs">
                  Pick a person and task from the list to start chatting
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskBuddies;
